import {FALLBACK_LANGUAGE, i18n} from '@online-offer/i18n';
import {ErrorBoundary} from '@sentry/react';
import {Show} from 'platform/foundation';

import {Outlet, useLocation, useParams} from 'react-router-dom';

import {any, isNotNil} from 'ramda';
import {isFalse, isNilOrEmpty, isTrue} from 'ramda-adjunct';

import {ErrorSection} from '../../(sections)/ErrorSection/ErrorSection';
import {Loading} from '../../(sections)/Loading/Loading';
import {NotFoundPage} from '../../(sections)/NotFoundPage/NotFoundPage';
import {useGetAllApiEnumsQuery, useGetVehicleAuditQuery} from '../../api/digitalCertificateApi';
import {useGetBusinessCaseOnlineOfferQuery, useListLogosQuery} from '../../api/onlineOfferApi';
import {queryParams} from '../../consts/queryParams';
import {getOnlineOfferSessionId} from '../../utils/getOnlineOfferSessionId';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {isOnlineOfferPdfRoute} from '../../utils/isOnlineOfferPdfRoute';
import {useQueryState} from '../../utils/useQueryState';
import {ApplicationInitializeEvent} from '../ApplicationInitializeEvent/ApplicationInitializeEvent';
import {OnlineOfferEngagementTracker} from '../OnlineOfferEngagementTracker/OnlineOfferEngagementTracker';

const {workspace} = getWorkspaceFromUri();

export function ApplicationInitializeBoundary() {
  const params = useParams<{offerId: string}>();
  const location = useLocation();
  const [openedBy] = useQueryState(queryParams.OPENED_BY);
  const isPdfRoute = isOnlineOfferPdfRoute(location.pathname);
  const sessionId = isPdfRoute
    ? null
    : getOnlineOfferSessionId({
        offerId: params.offerId ?? '',
        openedBy: openedBy ?? null,
      });

  const {isFetching: isListLogosQueryFetching} = useListLogosQuery(
    {'X-Workspace': workspace ?? ''},
    {skip: isNilOrEmpty(workspace)}
  );

  const {
    data: onlineOfferData,
    isError: isOnlineOfferQueryErrored,
    isFetching: isOnlineOfferQueryFetching,
    isLoading: isOnlineOfferQueryLoading,
    error: onlineOfferQueryError,
  } = useGetBusinessCaseOnlineOfferQuery({
    offerId: params.offerId ?? '',
    workspace: workspace ?? '',
    openedBy: openedBy ?? null,
    sessionId,
  });

  const {
    data: alphaCatalogue,
    isError: isAlphaCatalogueErrored,
    isFetching: isAlphaCatalogueFetching,
  } = useGetAllApiEnumsQuery(
    {
      lang: [i18n.resolvedLanguage ?? FALLBACK_LANGUAGE],
      vehicleType: onlineOfferData?.vehicle?.type ? [onlineOfferData.vehicle.type] : undefined,
    },
    {skip: isOnlineOfferQueryLoading}
  );

  // Fetch digital certificate only if certificate(carAuditId) is present
  const {
    data: vehicleAudit,
    isError: isVehicleAuditErrored,
    isFetching: isVehicleAuditFetching,
  } = useGetVehicleAuditQuery(
    {auditId: onlineOfferData?.carAuditId ?? '', workspace: workspace ?? ''},
    {skip: isNilOrEmpty(onlineOfferData?.carAuditId)}
  );

  const isFetching = any(isTrue, [
    isOnlineOfferQueryFetching,
    isAlphaCatalogueFetching,
    isVehicleAuditFetching,
    isListLogosQueryFetching,
  ]);

  const isErrored = any(isTrue, [
    isOnlineOfferQueryErrored,
    isAlphaCatalogueErrored,
    isVehicleAuditErrored,
  ]);

  const is404Error =
    onlineOfferQueryError &&
    'status' in onlineOfferQueryError &&
    onlineOfferQueryError.status === 404;

  const isAppReady =
    isNotNil(onlineOfferData) &&
    isNotNil(alphaCatalogue) &&
    // Require loading of certificate only if certificate(carAuditId) is present
    (isNilOrEmpty(onlineOfferData?.carAuditId) || isNotNil(vehicleAudit));

  const getComponent = () => {
    if (is404Error) {
      return <NotFoundPage />;
    }
    if (isErrored) {
      return <ErrorSection />;
    }
    if (isFetching || isFalse(isAppReady)) {
      return <Loading />;
    }
    return <Outlet />;
  };

  return (
    <>
      <Show when={isAppReady && process.env.ENV_TYPE === 'prod'}>
        <ApplicationInitializeEvent
          key={params.offerId}
          offerId={params.offerId}
          workspace={workspace}
        />
      </Show>
      <Show when={isAppReady && isNotNil(sessionId) && !isPdfRoute}>
        <OnlineOfferEngagementTracker
          key={params.offerId}
          offerId={params.offerId ?? ''}
          workspace={workspace ?? ''}
          sessionId={sessionId ?? ''}
        />
      </Show>
      <ErrorBoundary fallback={<ErrorSection />}>{getComponent()}</ErrorBoundary>
    </>
  );
}
